import React, { useState, useEffect } from "react";
import {
  Plus,
  Settings,
  Clipboard,
  Eye,
  Code,
  X,
  MessageCircle,
  HelpCircle,
  Trash2,
  Pencil,
  Headphones as Support,
  ChevronDown,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import toast from "react-hot-toast";
import ApiService from "../../services/ApiService";
import useBillingStore from "../../store/useBillingStore";

function WidgetManager() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewWidget, setPreviewWidget] = useState(null);
  const [widgetForm, setWidgetForm] = useState({
    type: "faq",
    title: "Frequently Asked Questions",
    primaryColor: "#3B82F6",
    secondaryColor: "#6366F1",
    position: "bottom-right",
    greeting: "Hello! How can we help you today?",
    supportEnabled: true,
  });
  const [editingWidgetId, setEditingWidgetId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    primaryColor: "",
    secondaryColor: "",
    position: "bottom-right",
    supportEnabled: true,
    contactPosition: "bottom",
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const { billingInfo, fetchBillingInfo } = useBillingStore();

  useEffect(() => {
    loadWidgets();
  fetchBillingInfo();
  }, [fetchBillingInfo]);

  const tokensExceeded = Boolean(
    billingInfo &&
      billingInfo.limits &&
      billingInfo.usage &&
      billingInfo.usage.tokens >= billingInfo.limits.tokens
  );

  // Compute which billing quotas are exceeded and prepare friendly messages
  const getExceededDetails = () => {
    // Only show tokens and widget view counts for Widget Manager
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return [];
    const details = [];
    const tokensUsed = billingInfo.usage?.tokens;
    const tokensLimit = billingInfo.limits?.tokens;
    if (typeof tokensUsed === 'number' && typeof tokensLimit === 'number' && tokensUsed >= tokensLimit) {
      details.push({ key: 'tokens', title: 'AI Tokens', used: tokensUsed, limit: tokensLimit, why: 'AI tokens exhausted — chat and AI-powered widgets are limited. Upgrade to restore AI features.' });
    }
    // Some backends may report widget views under different keys; check common variants
    const viewsUsed = billingInfo.usage?.widget_views ?? billingInfo.usage?.views ?? billingInfo.usage?.view_count;
    const viewsLimit = billingInfo.limits?.widget_views ?? billingInfo.limits?.views ?? billingInfo.limits?.view_count;
    if (typeof viewsUsed === 'number' && typeof viewsLimit === 'number' && viewsUsed >= viewsLimit) {
      details.push({ key: 'widget_views', title: 'Widget Views', used: viewsUsed, limit: viewsLimit, why: 'Your widget view quota is exhausted — users may not be able to load or see widgets. Upgrade to increase view limits.' });
    }
    return details;
  };

  const loadWidgets = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getWidgets();
      setWidgets(data.widgets || []);
    } catch (error) {
      console.error("Failed to load widgets:", error);
      toast.error("Failed to load widgets");
    } finally {
      setLoading(false);
    }
  };

  

  const handleCreateWidget = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const configuration = {
        title: widgetForm.title,
        primaryColor: widgetForm.primaryColor,
        secondaryColor: widgetForm.secondaryColor,
        position: widgetForm.position,
        greeting: widgetForm.greeting,
        supportEnabled: widgetForm.supportEnabled,
      };

      const result = await ApiService.createWidget(
        widgetForm.type,
        configuration
      );

      if (result.success) {
        toast.success("Widget created successfully!");
        setShowCreateModal(false);
        setWidgetForm({
          type: "faq",
          title: "Frequently Asked Questions",
          primaryColor: "#3B82F6",
          secondaryColor: "#6366F1",
          position: "bottom-right",
          greeting: "Hello! How can we help you today?",
          supportEnabled: true,
        });
        loadWidgets();
      } else {
        toast.error("Failed to create widget");
      }
    } catch (error) {
      console.error("Widget creation error:", error);
      toast.error("Failed to create widget");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyEmbedCode = (embedCode) => {
    navigator.clipboard.writeText(embedCode);
    toast.success("Embed code copied to clipboard!");
  };

  const showPreview = (widget) => {
    setPreviewWidget(widget);
    setShowPreviewModal(true);
  };
  const getWidgetTypeIcon = (type) => {
    return type === "chat" ? MessageCircle : HelpCircle;
  };

  const getPreviewCode = (widget) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    return `<script src="${baseUrl}/api/widgets/${widget.widget_type}-widget.js?organization_id=${widget.organization_id}&widget_id=${widget.widget_id}"></script>`;
  };
  const handleDelete = async (widgetId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this widget? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const result = await ApiService.deleteWidget(widgetId);
      // If we reach here, deletion was successful
      toast.success("Widget deleted successfully");

      // Update local state instead of reloading
      setWidgets((prevWidgets) =>
        prevWidgets.filter((widget) => widget.widget_id !== widgetId)
      );
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.message || "Failed to delete widget");
    }
  };
  const toggleSupportButton = async (widgetId, supportEnabled) => {
    try {
      // Optimistically update the local state first
      setWidgets((prevWidgets) =>
        prevWidgets.map((widget) =>
          widget.widget_id === widgetId
            ? {
                ...widget,
                configuration: {
                  ...widget.configuration,
                  supportEnabled,
                },
              }
            : widget
        )
      );

      // Then update the backend
      await ApiService.updateWidgetSettings(widgetId, { supportEnabled });
      toast.success(
        `Support button ${supportEnabled ? "enabled" : "disabled"} successfully`
      );
    } catch (error) {
      console.error("Toggle support button error:", error);
      toast.error(error.message || "Failed to update support button setting");

      // Revert the optimistic update if the API call fails
      setWidgets((prevWidgets) =>
        prevWidgets.map((widget) =>
          widget.widget_id === widgetId
            ? {
                ...widget,
                configuration: {
                  ...widget.configuration,
                  supportEnabled: !supportEnabled,
                },
              }
            : widget
        )
      );
    }
  };

  const startEdit = (widget) => {
    setEditingWidgetId(widget.widget_id);
    setEditForm({
      title: widget.configuration?.title || "",
      primaryColor: widget.configuration?.primaryColor || "#3B82F6",
      secondaryColor: widget.configuration?.secondaryColor || "#6366F1",
      position:
        widget.widget_type === "chat"
          ? widget.configuration?.position || "bottom-right"
          : undefined,
      supportEnabled:
        widget.widget_type === "faq"
          ? widget.configuration?.supportEnabled ?? true
          : undefined,
      contactPosition:
        widget.widget_type === "faq"
          ? widget.configuration?.contactPosition || "bottom"
          : undefined,
      accordionIconStyle:
        widget.widget_type === "faq"
          ? widget.configuration?.accordionIconStyle || "plus-minus"
          : undefined,
    });
    setShowEditModal(true);
  };

  const saveEdit = async (widget_id) => {
    try {
      const widget = widgets.find((w) => w.widget_id === widget_id);
      const updateData = {
        title: editForm.title,
        primaryColor: editForm.primaryColor,
        secondaryColor: editForm.secondaryColor,
      };
      if (widget.widget_type === "chat") {
        updateData.position = editForm.position;
      }
      if (widget.widget_type === "faq") {
        updateData.supportEnabled = editForm.supportEnabled;
        updateData.contactPosition = editForm.contactPosition;
        updateData.accordionIconStyle = editForm.accordionIconStyle;
      }
      await ApiService.updateWidgetSettings(widget_id, updateData);
      toast.success("Widget updated!");
      setEditingWidgetId(null);
      setShowEditModal(false);
      loadWidgets();
    } catch (e) {
      toast.error("Failed to update widget");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Widget Manager
          </h2>
          <p className="text-gray-600 mt-2">
            Create and manage widgets for your website
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:-translate-y-0.5"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Widget
        </button>
      </div>
      {billingInfo && billingInfo.limits && billingInfo.usage && (() => {
        const exceeded = getExceededDetails();
        if (!exceeded || exceeded.length === 0) return null;
        return (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50/60 backdrop-blur p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 rounded-lg bg-red-100 flex items-center justify-center border border-red-200">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-red-900">
                      Usage limit reached
                    </h4>
                    <p className="text-sm text-red-700/90 mt-0.5">
                      You've reached the following monthly quota{exceeded.length>1?'s':''}. Upgrade to increase limits and re-enable affected features.
                    </p>
                  </div>
                  <a
                    href="/billing"
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 text-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-red-700 transition-colors"
                  >
                    View billing & upgrade
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {exceeded.map((d) => {
                    const percent = d.limit > 0 ? Math.min(100, Math.round((d.used / d.limit) * 100)) : 100;
                    return (
                      <div key={d.key} className="rounded-lg border border-red-200 bg-white/60 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium uppercase tracking-wide text-red-700/80">
                            {d.title}
                          </span>
                          <span className="text-[11px] font-semibold text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                            {d.used} / {d.limit}
                          </span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-red-100 border border-red-200 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-red-500 to-red-600"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs text-red-700/90 leading-relaxed">
                          {d.why}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()} 
      {/* Widget Grid */}
      {widgets.length === 0 ? (
        <div className="text-center py-16">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl"></div>
            <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
              <Settings className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No widgets yet
          </h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            Create your first widget to get started.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:-translate-y-0.5"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Widget
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {widgets.map((widget) => {
            const Icon = getWidgetTypeIcon(widget.widget_type);
            const isEditing = editingWidgetId === widget.widget_id;
            return (
              <div
                key={widget.widget_id}
                className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg mr-3 group-hover:scale-110 transition-transform duration-200">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-xs uppercase tracking-wide text-gray-500 font-semibold bg-gray-100 px-2 py-1 rounded-full">
                        {widget.widget_type} Widget
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => showPreview(widget)}
                        className="text-gray-400 hover:text-blue-500 p-2 rounded-lg hover:bg-blue-50 transition-all duration-200"
                        title="Preview Widget"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    {isEditing ? (
                      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 items-start sm:items-center w-full">
                        <input
                          className="border rounded px-2 py-1 text-lg font-semibold text-gray-900 mb-1 sm:mb-0 flex-1 min-w-0"
                          value={editForm.title}
                          onChange={(e) =>
                            setEditForm({ ...editForm, title: e.target.value })
                          }
                          style={{ maxWidth: "220px" }}
                        />
                      </div>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200">
                          {widget.configuration?.title || "Untitled Widget"}
                        </h3>
                        <div className="flex items-center">
                          <button
                            onClick={() => startEdit(widget)}
                            className="text-gray-400 hover:text-blue-500 p-2 rounded-lg hover:bg-blue-50 transition-all duration-200"
                            title="Edit Widget"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(widget.widget_id)}
                            className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-all duration-200"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="space-y-2 mb-4">
                    {widget.widget_type === "chat" && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Position:</span>
                        {isEditing ? (
                          <select
                            className="border rounded px-2 py-1"
                            value={
                              editForm.position ||
                              widget.configuration?.position ||
                              "bottom-right"
                            }
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                position: e.target.value,
                              })
                            }
                            style={{ maxWidth: "130px" }}
                          >
                            <option value="bottom-right">Bottom Right</option>
                            <option value="bottom-left">Bottom Left</option>
                            <option value="top-right">Top Right</option>
                            <option value="top-left">Top Left</option>
                          </select>
                        ) : (
                          <span className="text-gray-700 bg-gray-100 px-2 py-1 rounded-full text-xs">
                            {widget.configuration?.position || "bottom-right"}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Created:</span>
                      <span className="text-gray-700">
                        {new Date(widget.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Color:</span>
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                          style={{
                            backgroundColor:
                              widget.configuration?.primaryColor || "#3B82F6",
                          }}
                        ></div>
                        <div
                          className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                          style={{
                            backgroundColor:
                              widget.configuration?.secondaryColor || "#6366F1",
                          }}
                        ></div>
                        {isEditing && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Primary & Secondary Colors
                              </label>
                              <div className="flex flex-row gap-4 flex-nowrap">
                                <div className="flex-1 flex flex-col items-center">
                                  <span className="text-xs text-gray-500 mb-1">
                                    Primary
                                  </span>
                                  <input
                                    type="color"
                                    value={editForm.primaryColor}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        primaryColor: e.target.value,
                                      })
                                    }
                                    className="w-full h-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                                    style={{ minWidth: 0, maxWidth: "100px" }}
                                  />
                                </div>
                                <div className="flex-1 flex flex-col items-center">
                                  <span className="text-xs text-gray-500 mb-1">
                                    Secondary
                                  </span>
                                  <input
                                    type="color"
                                    value={editForm.secondaryColor}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        secondaryColor: e.target.value,
                                      })
                                    }
                                    className="w-full h-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                                    style={{ minWidth: 0, maxWidth: "100px" }}
                                  />
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {widget.widget_type === "faq" && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">
                          Enable Contact Support Button:
                        </span>
                        {isEditing ? (
                          <input
                            type="checkbox"
                            checked={editForm.supportEnabled}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                supportEnabled: e.target.checked,
                              })
                            }
                            className="ml-2"
                          />
                        ) : (
                          <span className="ml-2 text-xs font-semibold text-green-600">
                            {widget.configuration?.supportEnabled
                              ? "Enabled"
                              : "Disabled"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => copyEmbedCode(getPreviewCode(widget))}
                    className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 font-medium rounded-xl border border-gray-200 hover:border-gray-300 hover:from-gray-100 hover:to-gray-200 transition-all duration-200"
                  >
                    <Clipboard className="h-4 w-4 mr-2" />
                    Copy Embed Code
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}{" "}
      {/* Widget Preview Section */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <h3 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Widget Types
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="group border border-gray-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg mr-3 group-hover:scale-110 transition-transform duration-200">
                  <HelpCircle className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">
                  FAQ Widget
                </h4>
              </div>
              <p className="text-gray-600 mb-4">
                Display frequently asked questions in an interactive accordion
                widget.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                  Searchable FAQ interface
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                  SEO FAQ Schema included
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                  Customizable styling
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                  Mobile responsive
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                  AI traffic tracking
                </li>
              </ul>
            </div>

            <div className="group border border-gray-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg mr-3 group-hover:scale-110 transition-transform duration-200">
                  <MessageCircle className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">
                  Chat Widget
                </h4>
              </div>
              <p className="text-gray-600 mb-4">
                AI-powered chat widget that provides instant answers based on
                your content.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                  AI-powered responses
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                  Real-time chat interface
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                  Context-aware answers
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                  Support query capture
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                  Email notifications
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Create Widget Modal - Redesigned with Preview */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 ">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
              {/* Left: Form */}
              <div className="md:col-span-1">
                <div className="flex justify-between items-center mb-6">
                  <h1 className="text-2xl font-bold text-gray-800">
                    Create New Widget
                  </h1>
                  <button
                    className="text-gray-400 hover:text-gray-600"
                    onClick={() => setShowCreateModal(false)}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <form onSubmit={handleCreateWidget}>
                  <div className="mb-6">
                    <div className={`grid gap-4 ${widgetForm.type === "chat" ? "grid-cols-2" : "grid-cols-1"}`}>
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-2"
                          htmlFor="widget-type"
                        >
                          Widget Type
                        </label>
                        <div className="relative">
                          <select
                            id="widget-type"
                            value={widgetForm.type}
                            onChange={(e) => {
                              const newType = e.target.value;
                              setWidgetForm({
                                ...widgetForm,
                                type: newType,
                                title:
                                  newType === "chat"
                                    ? "Customer Support"
                                    : "Frequently Asked Questions",
                              });
                            }}
                            className="w-full bg-white border border-gray-300 rounded-md py-3 px-4 pr-10 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                          >
                            <option value="faq">FAQ Widget</option>
                            <option value="chat">
                              Chat Widget
                            </option>
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                      {widgetForm.type === "chat" && (
                        <div>
                          <label
                            className="block text-sm font-medium text-gray-700 mb-2"
                            htmlFor="position"
                          >
                            Position
                          </label>
                          <div className="relative">
                            <select
                              id="position"
                              value={widgetForm.position}
                              onChange={(e) =>
                                setWidgetForm({
                                  ...widgetForm,
                                  position: e.target.value,
                                })
                              }
                              className="w-full bg-white border border-gray-300 rounded-md py-3 px-4 pr-10 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                            >
                              <option value="bottom-right">Bottom Right</option>
                              <option value="bottom-left">Bottom Left</option>
                              <option value="top-right">Top Right</option>
                              <option value="top-left">Top Left</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mb-6">
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2"
                      htmlFor="title"
                    >
                      Title
                    </label>
                    <input
                      id="title"
                      type="text"
                      value={widgetForm.title}
                      onChange={(e) =>
                        setWidgetForm({ ...widgetForm, title: e.target.value })
                      }
                      className="w-full bg-white border border-gray-300 rounded-md py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="mb-6">
                    <p className="block text-sm font-medium text-gray-700 mb-2">
                      Primary &amp; Secondary Color
                    </p>
                    <div className="flex space-x-6">
                      <div className="flex-1">
                        <label
                          className="block text-xs text-gray-500 text-center mb-1"
                          htmlFor="primary-color"
                        >
                          Primary
                        </label>
                        <input
                          id="primary-color"
                          type="color"
                          value={widgetForm.primaryColor}
                          onChange={(e) =>
                            setWidgetForm({
                              ...widgetForm,
                              primaryColor: e.target.value,
                            })
                          }
                          className="w-full h-12 p-1 border border-gray-300 rounded-md cursor-pointer"
                        />
                        <div className="mt-2 text-center text-sm text-gray-600">
                          {widgetForm.primaryColor.toUpperCase()}
                        </div>
                      </div>
                      <div className="flex-1">
                        <label
                          className="block text-xs text-gray-500 text-center mb-1"
                          htmlFor="secondary-color"
                        >
                          Secondary
                        </label>
                        <input
                          id="secondary-color"
                          type="color"
                          value={widgetForm.secondaryColor}
                          onChange={(e) =>
                            setWidgetForm({
                              ...widgetForm,
                              secondaryColor: e.target.value,
                            })
                          }
                          className="w-full h-12 p-1 border border-gray-300 rounded-md cursor-pointer"
                        />
                        <div className="mt-2 text-center text-sm text-gray-600">
                          {widgetForm.secondaryColor.toUpperCase()}
                        </div>
                      </div>
                    </div>
                  </div>
                  {widgetForm.type === "chat" && (
                    <>
                      <div className="mb-6">
                        <label
                          className="block text-sm font-medium text-gray-700 mb-2"
                          htmlFor="greeting"
                        >
                          Greeting Message
                        </label>
                        <input
                          id="greeting"
                          type="text"
                          value={widgetForm.greeting}
                          onChange={(e) =>
                            setWidgetForm({
                              ...widgetForm,
                              greeting: e.target.value,
                            })
                          }
                          className="w-full bg-white border border-gray-300 rounded-md py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </>
                  )}
                  {widgetForm.type === "faq" && (
                    <div className="mb-6">
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <label className="flex items-start justify-between cursor-pointer">
                          <div className="flex-1 pr-4">
                            <p className="text-sm font-medium text-gray-800 mb-1">
                              Enable Contact Support Button
                            </p>
                            <p className="text-xs text-gray-600 leading-relaxed">
                              When enabled, users can contact your support team
                              directly from the FAQ widget
                            </p>
                          </div>
                          <div className="relative flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={widgetForm.supportEnabled}
                              onChange={() =>
                                setWidgetForm({
                                  ...widgetForm,
                                  supportEnabled: !widgetForm.supportEnabled,
                                })
                              }
                              className="sr-only"
                            />
                            <div
                              className={`w-12 h-6 ${
                                widgetForm.supportEnabled
                                  ? "bg-blue-600"
                                  : "bg-gray-300"
                              } rounded-full transition-colors duration-200`}
                            ></div>
                            <div
                              className="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 shadow-sm"
                              style={{
                                transform: widgetForm.supportEnabled
                                  ? "translateX(100%)"
                                  : "translateX(0)",
                              }}
                            ></div>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}
                    <div className="flex justify-end space-x-4 mt-8">
                    <button
                      type="button"
                      className="px-6 py-2.5 bg-gray-200 text-gray-700 font-semibold rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Create Widget"}
                    </button>
                  </div>
                  
                </form>
              </div>
              {/* Right: Live Preview */}
              <div className="md:col-span-1 p-4 border border-gray-200 rounded-lg bg-gray-50 flex flex-col justify-center">
                <div className="flex justify-end mt-4">
                  <div className="max-w-[calc(100%-2rem)]">
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 w-150 max-w-full max-h-100 overflow-hidden">
                      <div className="p-0">
                        {widgetForm.type === "faq" ? (
                          <div className="space-y-4 px-4 py-4 md:px-6 md:py-6">
                            <div className="text-center pt-2 mb-2">
                              <div
                                className="text-xs font-semibold tracking-wider mb-1"
                                style={{
                                  color: widgetForm.primaryColor || "#3B82F6",
                                }}
                              >
                                GOT QUESTIONS?
                              </div>
                              <h3 className="text-lg font-bold text-gray-900 mb-2">
                                {widgetForm.title ||
                                  "Frequently Asked Question"}
                              </h3>
                            </div>
                            <div className="border-t border-gray-200 my-3"></div>
                            {/* Search Bar */}
                            <div className="mb-2">
                              <input
                                type="text"
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                                placeholder="Search frequently asked questions..."
                                disabled
                              />
                            </div>
                            {/* FAQ Items (reduced for compact preview) */}
                            <div className="space-y-2">
                              <div className="text-sm">
                                <div className="font-normal text-gray-700 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg shadow-sm">
                                  What is the AEO Platform and how does it work?
                                </div>
                              </div>
                              <div className="text-sm">
                                <div className="font-normal text-gray-700 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg shadow-sm">
                                  How does the AEO Platform improve my website's
                                  SEO?
                                </div>
                              </div>
                              <div className="text-sm">
                                <div className="font-normal text-gray-700 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg shadow-sm">
                                  What is RAG technology and how does the AEO
                                  Platform use it?
                                </div>
                              </div>
                            </div>
                            <div className="border-t border-gray-200 my-3"></div>
                            <div className="pt-2 text-center">
                              <div className="flex items-center justify-center mb-2">
                                {/* <div className="flex items-center text-xs text-gray-500">
                                  <span className="mr-1">⏱</span>
                                  <span>Showing 1-3 of 30</span>
                                </div> */}
                                <div className="flex items-center justify-center space-x-1">
                                  <button className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50">
                                    Prev
                                  </button>
                                  <button
                                    className="px-2 py-1 text-xs text-white rounded-full w-6 h-6 flex items-center justify-center font-medium"
                                    style={{
                                      background: `linear-gradient(90deg, ${
                                        widgetForm.primaryColor || "#E91E63"
                                      }, ${
                                        widgetForm.secondaryColor || "#1A1A2E"
                                      })`,
                                      border: "none",
                                    }}
                                  >
                                    1
                                  </button>
                                  <button className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded">
                                    2
                                  </button>
                                  <button className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded">
                                    3
                                  </button>
                                  <button className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800">
                                    Next
                                  </button>
                                </div>
                              </div>
                              <div className="mt-2 text-center">
                                <div className="text-xs text-gray-500 mb-2">
                                  Can't find what you're looking for?
                                </div>
                                <button
                                  className="px-4 py-2 text-xs text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                                  style={{
                                    background: `linear-gradient(90deg, ${
                                      widgetForm.primaryColor || "#E91E63"
                                    }, ${
                                      widgetForm.secondaryColor || "#1A1A2E"
                                    })`,
                                  }}
                                >
                                  Contact Our Support Team
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="w-[340px] min-h-[420px] bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden flex flex-col">
                            {/* Header */}
                            <div
                              className="flex items-center justify-between px-5 py-3 text-white"
                              style={{
                                background: `linear-gradient(135deg, ${
                                  widgetForm.primaryColor || "#3B82F6"
                                }, ${widgetForm.secondaryColor || "#6366F1"})`,
                              }}
                            >
                              <span className="font-semibold text-base">
                                {widgetForm.title || "Customer Support"}
                              </span>
                              <button className="p-1.5 hover:opacity-80 transition-opacity">
                                {/* Close Icon */}
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  stroke="white"
                                  strokeWidth="2"
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              </button>
                            </div>

                            {/* Greeting */}
                            <div className="px-5 py-4">
                              <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-700 text-sm shadow-sm">
                                {widgetForm.greeting ||
                                  "Hello! How can we help you today?"}
                              </div>
                            </div>

                            {/* Chat area (empty for preview) */}
                            <div className="flex-1 flex flex-col justify-end px-5"></div>

                            
                            <div className="flex items-center gap-2 px-5 mb-3 text-sm">
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="flex-shrink-0"
                              >
                                <path
                                  d="M12 12c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19c0 0.55 0.45 1 1 1h12c0.55 0 1-0.45 1-1v-1.5c0-2.33-4.67-3.5-7-3.5z"
                                  fill={widgetForm.primaryColor}
                                />
                              </svg>
                              <span
                                className="font-medium cursor-pointer bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] bg-clip-text text-transparent"
                                style={{
                                  "--primary": widgetForm.primaryColor || "#3B82F6",
                                  "--secondary": widgetForm.secondaryColor || "#6366F1",
                                  backgroundImage: `linear-gradient(90deg, ${widgetForm.primaryColor || "#3B82F6"}, ${widgetForm.secondaryColor || "#6366F1"})`,
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  backgroundClip: "text",
                                  color: "transparent",
                                }}
                              >
                                Talk to Human
                              </span>
                            </div>
                            <div className="relative w-full px-5 pb-4">
                              <input
                                type="text"
                                placeholder="Type your message here..."
                                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-full pl-4 pr-12 py-2.5 outline-none"
                                disabled
                              />
                              <button
                                className="absolute right-6 top-1/3 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center shadow-sm "
                                style={{
                                  background: `linear-gradient(90deg, ${
                                    widgetForm.primaryColor || "#3B82F6"
                                  }, ${
                                    widgetForm.secondaryColor || "#6366F1"
                                  })`,
                                }}
                                disabled
                              >
                                {/* Send Icon */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-send-horizontal-icon lucide-send-horizontal"><path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"/><path d="M6 12h16"/></svg>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Widget Preview Modal */}
      {showPreviewModal && previewWidget && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-5">
                <div className="flex justify-between items-center mb-5">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Embed Code
                  </h3>
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div>
                  {/* Preview iframe */}

                  {/* Embed code */}
                  <div className="space-y-4">
                    <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                      <pre>{getPreviewCode(previewWidget)}</pre>
                    </div>
                    <button
                      onClick={() =>
                        copyEmbedCode(getPreviewCode(previewWidget))
                      }
                      className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 font-medium rounded-xl border border-gray-200 hover:border-gray-300 hover:from-gray-100 hover:to-gray-200 transition-all duration-200"
                    >
                      <Clipboard className="h-4 w-4 mr-2" />
                      Copy Embed Code
                    </button>

                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <h5 className="font-medium text-blue-900 mb-2">
                        Features Included:
                      </h5>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• SEO FAQ Schema for better search visibility</li>
                        <li>• AI traffic tracking and analytics</li>
                        <li>• Mobile responsive design</li>
                        <li>• Auto-adapts to your website styling</li>
                        <li>• Cross-origin support</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEditModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
              {/* Left: Edit Form */}
              <div className="md:col-span-1">
                <div className="flex items-center justify-between mb-6">
                  <h1 className="text-2xl font-bold text-gray-800">
                    Edit Widget
                  </h1>
                  <button
                    className="text-gray-400 hover:text-gray-600"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingWidgetId(null);
                    }}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveEdit(editingWidgetId);
                  }}
                  className="space-y-6"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) =>
                        setEditForm({ ...editForm, title: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary & Secondary color
                    </label>
                    <div className="flex flex-row gap-4 flex-nowrap">
                      <div className="flex-1 flex flex-col items-center">
                        <span className="text-xs text-gray-500 mb-1">
                          Primary
                        </span>
                        <input
                          type="color"
                          value={editForm.primaryColor}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              primaryColor: e.target.value,
                            })
                          }
                          className="w-full h-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                          style={{ minWidth: 0, maxWidth: "100px" }}
                        />
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <span className="text-xs text-gray-500 mb-1">
                          Secondary
                        </span>
                        <input
                          type="color"
                          value={editForm.secondaryColor}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              secondaryColor: e.target.value,
                            })
                          }
                          className="w-full h-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                          style={{ minWidth: 0, maxWidth: "100px" }}
                        />
                      </div>
                    </div>
                    <div className="w-full flex justify-center">
                      <span className="mt-3 text-xs text-gray-400 flex items-center gap-1 whitespace-nowrap">
                        Click the
                        <span
                          className="inline-flex items-center align-middle relative"
                          style={{ top: "-1px" }}
                        >
                          <Eye className="h-5 w-5" />
                        </span>
                        button to preview your color changes.
                      </span>
                    </div>
                  </div>
                  {(() => {
                    const widget = widgets.find(
                      (w) => w.widget_id === editingWidgetId
                    );
                    if (widget?.widget_type === "chat") {
                      return (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Position
                          </label>
                          <div className="relative">
                            <select
                              value={editForm.position}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  position: e.target.value,
                                })
                              }
                              className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 appearance-none cursor-pointer"
                            >
                              <option value="bottom-right">Bottom Right</option>
                              <option value="bottom-left">Bottom Left</option>
                              <option value="top-right">Top Right</option>
                              <option value="top-left">Top Left</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                          </div>
                        </div>
                      );
                    } else if (widget?.widget_type === "faq") {
                      return (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Enable Contact Support Button
                          </label>
                          <div className="flex items-center">
                            <button
                              type="button"
                              onClick={() =>
                                setEditForm({
                                  ...editForm,
                                  supportEnabled: !editForm.supportEnabled,
                                })
                              }
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                editForm.supportEnabled
                                  ? "bg-blue-600"
                                  : "bg-gray-300"
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                  editForm.supportEnabled
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                }`}
                              />
                            </button>
                            <span className="ml-3 text-sm text-gray-600">
                              {editForm.supportEnabled ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            When enabled, users can contact your support team
                            directly from the FAQ widget
                          </p>
                          
                          {/* Contact Position Dropdown - Only show when support is enabled */}
                          {editForm.supportEnabled && (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contact Button Position
                              </label>
                              <div className="relative">
                                <select
                                  value={editForm.contactPosition || "bottom"}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      contactPosition: e.target.value,
                                    })
                                  }
                                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 appearance-none cursor-pointer"
                                >
                                  <option value="top">Contact at TOP</option>
                                  <option value="bottom">Contact at END</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                              </div>
                              <p className="mt-2 text-xs text-gray-500">
                                Position the "Can't find what you're looking for?" section
                              </p>
                            </div>
                          )}
                          
                          {/* Accordion Icon Style Toggle */}
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                              Accordion Icon Style
                            </label>
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() =>
                                  setEditForm({
                                    ...editForm,
                                    accordionIconStyle: editForm.accordionIconStyle === "arrow" ? "plus-minus" : "arrow",
                                  })
                                }
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                  editForm.accordionIconStyle === "arrow"
                                    ? "bg-blue-600"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                    editForm.accordionIconStyle === "arrow"
                                      ? "translate-x-6"
                                      : "translate-x-1"
                                  }`}
                                />
                              </button>
                              <span className="ml-3 text-sm text-gray-600">
                                {editForm.accordionIconStyle === "arrow" ? "↓ Arrow" : "+/− Plus/Minus"}
                              </span>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                              Toggle to switch between Arrow or Plus/Minus icons
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingWidgetId(null);
                      }}
                      className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>

              {/* Right: Live Preview */}
              <div className="md:col-span-1 p-4 border border-gray-200 rounded-lg bg-gray-50 flex flex-col justify-center">
                <div className="flex justify-end mt-4">
                  <div className="max-w-[calc(100%-2rem)]">
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 w-150 max-w-full max-h-100 overflow-hidden">
                      <div className="p-0">
                        {(() => {
                          const widget = widgets.find((w) => w.widget_id === editingWidgetId);
                          if (widget?.widget_type === "faq") {
                            return (
                              <div className="space-y-4 px-4 py-4 md:px-6 md:py-6">
                                <div className="text-center pt-2 mb-2">
                                  <div
                                    className="text-xs font-semibold tracking-wider mb-1"
                                    style={{
                                      color: editForm.primaryColor || "#3B82F6",
                                    }}
                                  >
                                    GOT QUESTIONS?
                                  </div>
                                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                                    {editForm.title || "Frequently Asked Questions"}
                                  </h3>
                                </div>
                                <div className="border-t border-gray-200 my-3"></div>
                                {/* Search Bar */}
                                <div className="mb-2">
                                  <input
                                    type="text"
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                                    placeholder="Search frequently asked questions..."
                                    disabled
                                  />
                                </div>
                                {/* FAQ Items (reduced for compact preview) */}
                                <div className="space-y-2">
                                  <div className="text-sm">
                                    <div className="font-normal text-gray-700 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg shadow-sm">
                                      What is the AEO Platform and how does it work?
                                    </div>
                                  </div>
                                  <div className="text-sm">
                                    <div className="font-normal text-gray-700 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg shadow-sm">
                                      How does the AEO Platform improve my website's SEO?
                                    </div>
                                  </div>
                                  <div className="text-sm">
                                    <div className="font-normal text-gray-700 flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg shadow-sm">
                                      What is RAG technology and how does the AEO Platform use it?
                                    </div>
                                  </div>
                                </div>
                                <div className="border-t border-gray-200 my-3"></div>
                                <div className="pt-2 text-center">
                                  <div className="flex items-center justify-center mb-2">
                                    <div className="flex items-center justify-center space-x-1">
                                      <button className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50">
                                        Prev
                                      </button>
                                      <button
                                        className="px-2 py-1 text-xs text-white rounded-full w-6 h-6 flex items-center justify-center font-medium"
                                        style={{
                                          background: `linear-gradient(90deg, ${
                                            editForm.primaryColor || "#E91E63"
                                          }, ${
                                            editForm.secondaryColor || "#1A1A2E"
                                          })`,
                                          border: "none",
                                        }}
                                      >
                                        1
                                      </button>
                                      <button className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded">
                                        2
                                      </button>
                                      <button className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded">
                                        3
                                      </button>
                                      <button className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800">
                                        Next
                                      </button>
                                    </div>
                                  </div>
                                  {editForm.supportEnabled && (
                                    <div className="mt-2 text-center">
                                      <div className="text-xs text-gray-500 mb-2">
                                        Can't find what you're looking for?
                                      </div>
                                      <button
                                        className="px-4 py-2 text-xs text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                                        style={{
                                          background: `linear-gradient(90deg, ${
                                            editForm.primaryColor || "#E91E63"
                                          }, ${
                                            editForm.secondaryColor || "#1A1A2E"
                                          })`,
                                        }}
                                      >
                                        Contact Our Support Team
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          } else if (widget?.widget_type === "chat") {
                            return (
                              <div className="w-[340px] min-h-[420px] bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden flex flex-col">
                                {/* Header */}
                                <div
                                  className="flex items-center justify-between px-5 py-3 text-white"
                                  style={{
                                    background: `linear-gradient(135deg, ${
                                      editForm.primaryColor || "#3B82F6"
                                    }, ${editForm.secondaryColor || "#6366F1"})`,
                                  }}
                                >
                                  <span className="font-semibold text-base">
                                    {editForm.title || "Customer Support"}
                                  </span>
                                  <button className="p-1.5 hover:opacity-80 transition-opacity">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      stroke="white"
                                      strokeWidth="2"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <line x1="18" y1="6" x2="6" y2="18" />
                                      <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                  </button>
                                </div>

                                {/* Greeting */}
                                <div className="px-5 py-4">
                                  <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-700 text-sm shadow-sm">
                                    Hello! How can we help you today?
                                  </div>
                                </div>

                                {/* Chat area (empty for preview) */}
                                <div className="flex-1 flex flex-col justify-end px-5"></div>

                                <div className="flex items-center gap-2 px-5 mb-3 text-sm">
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-shrink-0"
                                  >
                                    <path
                                      d="M12 12c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19c0 0.55 0.45 1 1 1h12c0.55 0 1-0.45 1-1v-1.5c0-2.33-4.67-3.5-7-3.5z"
                                      fill={editForm.primaryColor}
                                    />
                                  </svg>
                                  <span
                                    className="font-medium cursor-pointer bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] bg-clip-text text-transparent"
                                    style={{
                                      "--primary": editForm.primaryColor || "#3B82F6",
                                      "--secondary": editForm.secondaryColor || "#6366F1",
                                      backgroundImage: `linear-gradient(90deg, ${editForm.primaryColor || "#3B82F6"}, ${editForm.secondaryColor || "#6366F1"})`,
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent",
                                      backgroundClip: "text",
                                      color: "transparent",
                                    }}
                                  >
                                    Talk to Human
                                  </span>
                                </div>
                                <div className="relative w-full px-5 pb-4">
                                  <input
                                    type="text"
                                    placeholder="Type your message here..."
                                    className="w-full text-sm bg-gray-50 border border-gray-200 rounded-full pl-4 pr-12 py-2.5 outline-none"
                                    disabled
                                  />
                                  <button
                                    className="absolute right-6 top-1/3 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center shadow-sm"
                                    style={{
                                      background: `linear-gradient(90deg, ${
                                        editForm.primaryColor || "#3B82F6"
                                      }, ${
                                        editForm.secondaryColor || "#6366F1"
                                      })`,
                                    }}
                                    disabled
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <line x1="22" y1="2" x2="11" y2="13" />
                                      <polygon points="22,2 15,22 11,13 2,9 22,2" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WidgetManager;
